<template>
  <div>
    <ShortlistContext>
      <mq-layout class="cards" :mq="['break-xs', 'break-small']">
        <StartupListCard
          v-for="(startup, index) in startups"
          :key="index"
          :startup="startup"
          :startups-rating-value="startupsRatingValue"
          :open-rate-dialog="openRateDialog"
          :on-interest-changed="onInterestChanged"
          :promote-startup="promoteStartup"
          :demote-startup="demoteStartup"
          :toggle-pro-status="toggleProStatus"
          :start-following="startFollowing"
          :stop-following="stopFollowing"
          :shortlist-id="shortlistId"
        />
      </mq-layout>
      <mq-layout mq="break-medium+" class="col-sm-12">
        <el-table
          :data="startups"
          :row-class-name="getRowClassName"
          row-key="id"
          data-testid="startup-table"
        >
          <el-table-column label="Name" class-name="name">
            <span
              slot-scope="{ row }"
              class="company-name"
              data-testid="company-name"
            >
              <span v-if="row.is_obfuscated" class="obfuscated">
                {{ row.name }}
              </span>
              <a v-else :href="getStartupProfileLink(row)" target="_blank">
                <el-tooltip
                  v-if="
                    $hasFeature('ORG_NEW_STARTUPS') && isStartupNewToOrg(row)
                  "
                  placement="top"
                >
                  <div slot="content" class="text-center">
                    View profile to mark as seen<br />
                    Added at {{ row.added_at | date }}
                  </div>
                  <span class="new-startup-badge"> NEW </span>
                </el-tooltip>
                {{ row.name }}
              </a>
              <p
                v-if="user.isAdmin"
                class="text-white group-hover:text-gray-300"
              >
                Updated {{ row.updated_at | date }}
              </p>
            </span>
          </el-table-column>
          <el-table-column label="Location" show-overflow-tooltip>
            <template slot-scope="{ row }">
              <div
                class="text-ellipsis"
                :class="{ obfuscated: row.is_obfuscated }"
              >
                <span>{{ getLocationForStartup(row) }}</span>
              </div>
            </template>
          </el-table-column>
          <el-table-column label="Sectors" show-overflow-tooltip>
            <template slot-scope="{ row }">
              <div class="two-spans">
                <span :class="{ obfuscated: row.is_obfuscated }">
                  {{ getSectorsText(row) }}
                </span>
              </div>
            </template>
          </el-table-column>
          <el-table-column
            v-if="showActionsColumn && user.isInvestorOrOrganizer"
            label="Rating"
            header-align="center"
            align="center"
            width="140"
          >
            <template slot-scope="{ row }">
              <StartupRateButton
                v-if="user.role !== 'STARTUP'"
                :startup="row"
                :rate-value="startupsRatingValue[row.id]"
                :disabled="row.is_obfuscated"
                @click="openRateDialog({ startup: row })"
              />
            </template>
          </el-table-column>
          <el-table-column
            v-if="showActionsColumn"
            label="Actions"
            header-align="center"
            width="280"
          >
            <template slot-scope="{ row: startup }">
              <div class="flex flex-col">
                <div>
                  <ConnectWithStartup
                    v-if="user.isInvestorOrOrganizer"
                    :disabled="startup.is_obfuscated"
                    :startup="startup"
                    :org-id="user.organizationId"
                  />

                  <ShortlistStartup
                    v-if="user.isInvestorOrOrganizer"
                    :startup="startup"
                    :org-id="user.organizationId"
                    @update="
                      ({ shortlists }) =>
                        onStartupShortlistsUpdate({ shortlists, startup })
                    "
                  />
                </div>

                <el-tooltip v-if="user.isAdmin" content="Edit" placement="top">
                  <a
                    class="edit-wrapper btn btn-primary btn-xs"
                    :href="getNewAppUrl(`/admin/startups/${startup.id}`)"
                  >
                    <i class="ion ion-edit"></i>
                  </a>
                </el-tooltip>
              </div>
            </template>
          </el-table-column>
        </el-table>
      </mq-layout>

      <RateDialog
        ref="rateDialog"
        :title="`Rate startup: ${startupForRating.name}`"
        :startup-id="startupForRating.id"
        @rated="$emit('rated', $event)"
      />
    </ShortlistContext>
  </div>
</template>

<script>
import { mapState } from 'vuex'
import { notify } from 'services/notifications'
import { features } from 'utils/features'
import ConnectWithStartup from 'components/organizations/ConnectWithStartup'
import ShortlistStartup from 'components/organizations/ShortlistStartup/ShortlistStartup.vue'
import RateDialog from 'components/startups/RateDialog.vue'
import StartupListCard from '@/modules/startup-list/StartupListCard'
import StartupRateButton from 'components/startups/StartupRateButton.vue'
import { countryName } from 'services/countries'
import { sectorLabel } from 'services/sectors'
import { getLocationForStartup } from 'services/startup'
import ShortlistContext from '@/contexts/ShortlistContext.vue'
import { getNewAppUrl } from '@/utils/urls'

export default {
  components: {
    ConnectWithStartup,
    RateDialog,
    StartupListCard,
    StartupRateButton,
    ShortlistStartup,
    ShortlistContext,
  },
  filters: { countryName },
  inject: ['showroomId'],
  props: {
    startups: { type: Array, default: () => [] },
    showActions: { type: Boolean, default: false },
    shadowedCount: { type: Number, default: 0 },
    shortlistId: { type: String, default: null },
  },
  computed: {
    startupsRatingValue() {
      const ratingsPerStartup = {}
      this.startups.forEach((startup) => {
        // TODO: change to _.get(startup, 'ratings.overall.value')
        if (
          startup.ratings &&
          startup.ratings.overall &&
          startup.ratings.overall.value
        ) {
          ratingsPerStartup[startup.id] = startup.ratings.overall.value
        } else {
          ratingsPerStartup[startup.id] = null
        }
      })
      return ratingsPerStartup
    },
    isAtLeastOneActionVisible() {
      return (
        this.user.isAdmin ||
        features.STARTUP_FOLLOW ||
        this.user.isInvestorOrOrganizer
      )
    },
    showActionsColumn() {
      return this.showActions && this.isAtLeastOneActionVisible
    },
    ...mapState({
      user: (s) => s.user.data,
      showroom: (s) => s.eventDetails.event,
    }),
  },
  data() {
    return {
      getLocationForStartup,
      features: features,
      startupForRating: {
        ratings: {
          overall: { value: null, note: null },
        },
      },
    }
  },
  methods: {
    getNewAppUrl,
    startFollowing(startup) {
      this.$store.dispatch('startupList/startFollowing', { startup })
      this.$emit('follow', { startup })
    },

    stopFollowing(startup) {
      this.$store.dispatch('startupList/stopFollowing', { startup })
      this.$emit('unfollow', { startup })
    },

    toggleProStatus(startup) {
      if (startup.has_promode) {
        this.demoteStartup(startup)
      } else {
        this.promoteStartup(startup)
      }
    },

    async promoteStartup(startup) {
      await this.$store.dispatch('startupList/promote', { startup })
      notify({ title: 'Startup has been promoted', type: 'success' })
    },

    async demoteStartup(startup) {
      await this.$store.dispatch('startupList/demote', { startup })
      notify({ title: 'Startup has been demoted', type: 'success' })
    },

    onInterestChanged({ startupId, state }) {
      this.$emit('interest-changed', { startupId, state })
      const attrs = { interest_state: state }
      const params = { startupId, attrs }
      this.$store.commit('startupList/setStartupAttrs', params)
      this.$store.commit('eventDetails/setStartupAttrs', params)
    },

    openRateDialog({ startup }) {
      this.startupForRating = startup
      this.$refs.rateDialog.open(startup.ratings)
    },

    getStartupProfileLink(startup) {
      const params = { slug: startup.slug || startup.id }
      const query = {}
      if (this.showroomId) {
        query.showroomId = this.showroomId
      }
      return getNewAppUrl(
        this.$router.resolve({
          name: 'publicStartupProfile',
          params,
          query,
        }).href,
      )
    },

    getSectorsText(startup) {
      return startup.sectors.map((s) => sectorLabel(s)).join(', ')
    },

    onFollowChanged({ startup, follows }) {
      if (follows) {
        this.startFollowing(startup)
      } else {
        this.stopFollowing(startup)
      }
    },

    getRowClassName({ row }) {
      const classNames = ['startup-item group']
      if (row.is_obfuscated) {
        classNames.push('no-pointer-events')
        classNames.push('is-blurred')
      }
      return classNames.join(' ')
    },

    isStartupNewToOrg(row) {
      return this.$store.getters['startupList/isStartupNew'](row.id)
    },
    onStartupShortlistsUpdate({ shortlists, startup }) {
      if (this.shortlistId) {
        // we are at the specific shortlist page
        // if it's not within updated shortlsits then this startup should be removed
        if (!shortlists.map((s) => s.id).includes(this.shortlistId)) {
          this.$store.dispatch('startupList/removeStartup', {
            id: startup.id,
          })
        }
      }
    },
  },
}
</script>

<style scoped lang="scss">
@import '~assets/css/colors.scss';

.startup-list-items {
  width: 96%;
  margin: 0 auto;

  .center {
    text-align: center;
  }
}

.startup-item {
  .obfuscated {
    color: transparent;
    display: block;
    width: 100%;
    @extend .animated-background;
  }

  &.no-pointer-events {
    pointer-events: none;

    * {
      pointer-events: none;
    }
  }

  .name {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    max-width: 180px;
  }

  .center {
    text-align: center;
  }
  .no-wrap {
    white-space: nowrap;
  }

  .ion-thumbsup,
  .ion-thumbsdown {
    font-size: 13px;
    color: $black;
    margin-top: 4px;
  }

  td {
    vertical-align: middle;
    padding: 10px;
    font-size: 14px;

    .rate-button-group {
      min-width: 120px;
    }
  }

  .startup-description p {
    color: $grey60;
    margin: 0;
  }

  .two-spans {
    display: flex;
    align-items: center;
    min-height: 38px;

    div:last-child {
      display: flex;
      justify-content: center;
      flex-direction: column;
    }

    i {
      margin-top: -2px;
    }

    span {
      white-space: nowrap;
      text-overflow: ellipsis;
      overflow: hidden;
      max-width: 200px;
    }
  }
}

// Animation
@keyframes placeHolderShimmer {
  0% {
    background-position: -468px 0;
  }
  100% {
    background-position: 468px 0;
  }
}

.animated-background {
  animation-duration: 1.5s;
  animation-fill-mode: forwards;
  animation-iteration-count: infinite;
  animation-name: placeHolderShimmer;
  animation-timing-function: linear;
  background: #f6f6f6;
  background: linear-gradient(to right, #f6f6f6 8%, #f0f0f0 18%, #f6f6f6 33%);
  background-size: 800px 104px;
  // height: 96px;
  position: relative;
  will-change: background-position;
  transform: translateZ(0);
  backface-visibility: hidden;
  perspective: 1000px;
}
</style>
